
import { useState, useEffect } from 'react';
import { Wallet, LogIn, Sun, MoonStar, Calendar, Languages, MessageCircle, Heart } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@store/authStore';
import { useSettingsStore } from '@store/settingsStore';
import { clearAllCaches } from '@store/clearAllCaches';
import { authService } from '@services/authService';
import Logo from '@components/common/Logo';
import UserDropdown from '@components/common/UserDropdown';
import NotificationBell from '@components/common/NotificationBell';
import Select from '@components/common/Select';

interface Tab {
    id: string;
    label: string;
    icon: any;
}

interface NavItem {
    label: string;
    href: string;
    icon: any;
}

interface HeaderProps {
    title?: string;
    subtitle?: string;
    tabs?: Tab[];
    activeTab?: string;
    onTabChange?: (id: string) => void;
    rightAction?: React.ReactNode;
    navItems?: NavItem[];
    hideUserDropdown?: boolean;
}

const Header = ({
    title,
    subtitle,
    tabs = [],
    activeTab,
    onTabChange,
    rightAction,
    navItems,
    hideUserDropdown = false
}: HeaderProps) => {
    const { user, logout, isAuthenticated, updateUser } = useAuthStore();
    const { contentLanguage, setContentLanguage } = useSettingsStore();
    const navigate = useNavigate();
    const location = useLocation();

    // Initialize language from user preference or Zustand settings
    const [language, setLanguage] = useState(() => {
        if (user?.content_language) return user.content_language;
        return contentLanguage || 'English';
    });

    // Sync state when user profile or store settings update
    useEffect(() => {
        if (user?.content_language) {
            setLanguage(user.content_language);
        } else if (contentLanguage) {
            setLanguage(contentLanguage.charAt(0).toUpperCase() + contentLanguage.slice(1));
        }
    }, [user?.content_language, contentLanguage]);

    const handleLanguageChange = async (newLang: string) => {
        const normalizedLang = newLang.toLowerCase() === 'hindi' ? 'Hindi' : 'English';
        const langValue = normalizedLang.toLowerCase() as 'hindi' | 'english';

        setLanguage(normalizedLang);
        setContentLanguage(langValue);

        if (isAuthenticated && user) {
            // Only update content_language, NOT preferred_language
            // preferred_language is a separate user preference for chat/AI responses
            updateUser({
                content_language: normalizedLang
            });
            try {
                await authService.updateProfile({
                    content_language: langValue
                });
            } catch (e) {
                console.error("Failed to sync language preference", e);
            }
        }
    };

    const handleLogout = async () => {
        try {
            await authService.logout();
        } catch (error) {
            console.error("Logout error:", error);
        }
        clearAllCaches();
        logout();
        // ProtectedRoute will handle redirect to /login
    };

    const isDashboard = location.pathname.startsWith('/dashboard') ||
        location.pathname.startsWith('/admin') ||
        location.pathname.startsWith('/chat') ||
        location.pathname.startsWith('/profile');
    const isChatPage = location.pathname.startsWith('/chat');

    const isPublicServicePage = [
        '/free-kundli',
        '/kundli-matching',
        '/panchang',
        '/horoscope',
        '/transit-search'
    ].some(path => location.pathname.startsWith(path));

    const internalNavItems: NavItem[] = navItems || [
        { label: 'Kundli', href: '/free-kundli', icon: Sun },
        { label: 'Matching', href: '/kundli-matching', icon: Heart },
        { label: 'Horoscopes', href: '/horoscope', icon: MoonStar },
        { label: 'Panchang', href: '/panchang', icon: Calendar },
        { label: 'Discussion', href: '/discussion', icon: MessageCircle },
    ];

    const NavButton = ({ id, label, icon: Icon, active = false }: { id: string, label: string, icon: any, active?: boolean }) => (
        <button
            onClick={() => onTabChange?.(id)}
            className={`relative flex items-center gap-2 px-3 md:px-4 py-2 rounded-full transition-colors duration-300 group ${active
                ? 'text-white bg-white/15'
                : 'text-purple-100/70 hover:text-white hover:bg-white/10'
                }`}
        >
            <Icon
                size={16}
                className={`transition-colors duration-300 ${active ? 'text-purple-300' : 'text-purple-300/60 group-hover:text-purple-300'}`}
            />
            <span className="text-xs font-bold tracking-[0.2em] uppercase hidden lg:block font-sans">
                {label}
            </span>
        </button>
    );

    const languageOptions = [
        { value: 'English', label: 'English' },
        { value: 'Hindi', label: 'हिंदी' }
    ];

    // Determine current language value for Select
    const currentLangValue = language.toLowerCase() === 'hindi' ? 'Hindi' : 'English';

    return (
        <header className={`px-3 md:px-6 lg:px-8 pb-3.5 md:pb-3 pt-[calc(0.875rem+env(safe-area-inset-top,0px))] md:pt-[calc(0.75rem+env(safe-area-inset-top,0px))] min-h-[72px] md:min-h-0 border-b border-white/10 bg-cosmic-900/90 backdrop-blur-2xl flex justify-between items-center shrink-0 relative z-[60] transition-all duration-300 sticky top-0 w-full`} style={undefined}>
            {/* Left: Logo & Title */}
            <div className="flex items-center gap-2 md:gap-5 z-20">
                <div className="cursor-pointer" onClick={() => navigate(isDashboard ? '/dashboard' : '/')}>
                    <Logo
                        showSubheading={!isDashboard}
                        imageSize={isDashboard ? "w-9 h-9 md:w-11 md:h-11" : "w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10"}
                        textSize={isDashboard ? "text-xl md:text-2xl" : "text-sm sm:text-base md:text-2xl"}
                        subheadingSize={isDashboard ? "text-[10px]" : "text-[8px] sm:text-[10px]"}
                        isLink={false}
                        showText={!isDashboard}
                        className="pointer-events-none"
                        showOnlineIndicator={isDashboard}
                    />
                </div>
                {isDashboard && title && (
                    <div className="flex flex-col justify-center border-l border-white/10 pl-3 md:pl-4 min-h-[28px] h-auto md:h-10">
                        <h1 className="text-sm md:text-lg lg:text-xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-purple-100 via-purple-300 to-purple-500 tracking-wide font-serif leading-tight pb-1">
                            {title}
                        </h1>
                        {subtitle && (
                            <p className="flex text-[9px] md:text-[10px] text-purple-300/60 uppercase tracking-[0.15em] md:tracking-[0.2em] font-sans font-bold whitespace-nowrap items-center gap-1.5">
                                {location.pathname.startsWith('/chat') && (
                                    <span className="relative flex h-1.5 w-1.5">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-purple-500 shadow-[0_0_8px_rgba(168,85,247,0.8)]"></span>
                                    </span>
                                )}
                                {subtitle}
                            </p>
                        )}
                    </div>
                )}
            </div>

            {/* Center: Tabs or NavLinks */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden lg:flex items-center gap-1 lg:gap-2">
                {tabs.length > 0 && tabs.map((tab) => (
                    <NavButton
                        key={tab.id}
                        id={tab.id}
                        label={tab.label}
                        icon={tab.icon}
                        active={activeTab === tab.id}
                    />
                ))}

                {tabs.length === 0 && internalNavItems.length > 0 && internalNavItems.map((item) => (
                    <Link
                        key={item.label}
                        to={item.href}
                        className={`group flex items-center gap-2 text-[11px] font-bold tracking-[0.15em] md:tracking-[0.2em] uppercase px-3 md:px-4 py-2 rounded-full transition-colors font-sans whitespace-nowrap ${location.pathname === item.href
                            ? 'text-white bg-white/10'
                            : 'text-purple-300/60 hover:text-white hover:bg-white/5'
                            }`}
                    >
                        <item.icon
                            size={16}
                            className={`transition-colors duration-300 ${location.pathname === item.href
                                ? (item.label === 'Admin' ? 'text-yellow-400' : 'text-purple-300')
                                : (item.label === 'Admin' ? 'text-yellow-500/50 group-hover:text-yellow-400' : 'text-purple-300/60 group-hover:text-purple-300')}`}
                        />
                        {item.label}
                    </Link>
                ))}
            </div>

            {/* Right: Actions + Wallet + User */}
            <div className="flex items-center gap-2 md:gap-3 z-20 [&>*]:h-8 [&>*]:md:h-auto">
                {isPublicServicePage && (
                    <Select
                        value={currentLangValue}
                        onChange={handleLanguageChange}
                        options={languageOptions}
                        searchable={false}
                        size="sm"
                        fullWidth={false}
                        prefixIcon={<Languages size={16} className="md:w-3.5 md:h-3.5" />}
                        className="[&>button]:!h-8 [&>button]:md:!h-auto [&>button]:!bg-white/[0.08] [&>button]:!border-white/20 [&>button]:!rounded-full [&>button]:hover:!bg-white/[0.15] [&>button]:hover:!border-purple-400/50 [&>button]:transition-all [&>button]:duration-200 [&>button]:!px-2.5 [&>button]:md:!px-4 [&>button]:!py-1.5 [&>button]:md:!py-2 [&_.truncate]:hidden md:[&_.truncate]:block"
                    />
                )}

                {isAuthenticated && (
                    <>
                        <button
                            onClick={() => navigate('/profile/payments')}
                            className="flex items-center gap-1.5 md:gap-2 px-2.5 md:px-4 h-8 md:h-auto md:py-2 bg-white/[0.08] border border-white/20 rounded-full hover:bg-white/[0.15] hover:border-purple-400/50 transition-colors duration-200 group cursor-pointer">
                            <Wallet size={16} className="md:w-3.5 md:h-3.5 text-purple-300 group-hover:text-purple-200 transition-colors" />
                            <span className="text-xs md:text-sm font-bold text-white group-hover:text-purple-50 tracking-wide transition-colors">₹{user?.wallet_balance?.toLocaleString('en-IN', { maximumFractionDigits: 0 }) || '0'}</span>
                        </button>
                        {isDashboard && !isChatPage && <NotificationBell />}
                    </>
                )}

                {rightAction && (
                    <div className="[&>button]:h-8 [&>button]:md:h-auto [&>button]:flex [&>button]:items-center">
                        {rightAction}
                    </div>
                )}

                {!hideUserDropdown && (
                    <>
                        {isAuthenticated ? (
                            <UserDropdown user={user} onLogout={handleLogout} />
                        ) : (
                            <Link to="/login" className="flex items-center gap-1.5 md:gap-2 px-2.5 md:px-4 h-8 md:h-auto md:py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded-full border border-white/20 transition-all whitespace-nowrap">
                                <LogIn size={16} className="md:w-3.5 md:h-3.5" />
                                <span className="text-xs md:text-sm font-bold tracking-wide">Get Started</span>
                            </Link>
                        )}
                    </>
                )}
            </div>
        </header>
    );
};


export default Header;
